import {
	BellOutlined,
	CheckCircleOutlined,
	DownOutlined,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Badge } from "components/Badge/Badge"
import { Button } from "components/Button/Button"
import { Dropdown } from "components/Dropdown/Dropdown"
import { ItemType } from "components/Menu/Menu"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { Wrap } from "components/Wrap/Wrap"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import {
	NOTIFICATION_FEED_STATUS,
	Notification,
	NotificationFeedStatus,
} from "interface/notification"
import { User } from "interface/user"
import { useFlag } from "modules/flags/flags"
import { ReactNode, useCallback, useState } from "react"
import { Link } from "react-router"
import {
	useNotifications,
	useNotificationsUnreadCount,
	useReadAllNotifications,
} from "services/notifications"
import { useUserRole } from "services/users"
import { humanReadable } from "utils"
import { asDate } from "utils/date"

const NOTIFICATION_STATUSES: ItemType[] = NOTIFICATION_FEED_STATUS.map(
	(key) => ({
		key,
		label: humanReadable(key),
	}),
)

export function Notifications() {
	const { clinicalEscalations } = useFlag("clinicalEscalations")
	const { data: currentUser } = useUserRole()
	const { data: { unreadCount = 0 } = {} } = useNotificationsUnreadCount(
		clinicalEscalations.enabled,
	)

	const renderPopup = useCallback(
		() =>
			currentUser ? (
				<Popup currentUser={currentUser} unreadCount={unreadCount} />
			) : null,
		[currentUser, unreadCount],
	)

	if (!clinicalEscalations.enabled || !currentUser) {
		return null
	}

	return (
		<Dropdown
			trigger={["click"]}
			rootClassName="w-105"
			destroyOnHidden
			popupRender={renderPopup}
		>
			<Badge
				count={unreadCount}
				offset={[unreadCount >= 10 ? 4 : 0, 0]}
				classNames={{ indicator: "bg-red-40" }}
			>
				<Button
					shape="round"
					type="primary"
					aria-label={
						unreadCount > 0
							? `Notifications, ${unreadCount} unread`
							: "Notifications"
					}
					icon={<BellOutlined aria-hidden />}
				/>
			</Badge>
		</Dropdown>
	)
}

function Popup({
	currentUser,
	unreadCount,
}: {
	currentUser: User
	unreadCount: number
}) {
	const { notify } = useNotification()
	const [targetScroll, setTargetScroll] = useState<HTMLUListElement | null>(
		null,
	)
	const [status, setStatus] = useState<NotificationFeedStatus>("all")
	const {
		data: { pages: notifications = [] } = {},
		hasNextPage,
		isLoading,
		fetchNextPage,
	} = useNotifications(status)
	const readAll = useReadAllNotifications()
	const handleReadAll = () => {
		readAll.mutate(undefined, {
			onSuccess() {
				setStatus("all")
			},
			onError(error) {
				notify.error({ title: error.message })
			},
		})
	}

	useInfiniteScroll({
		target: targetScroll,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<div className="flex max-h-[min(--spacing(133),calc(100dvh-(--spacing(20))))] flex-col overflow-hidden rounded-2xl bg-white py-4 shadow">
			<div className="flex items-center gap-6 px-4">
				<h2 className="text-base font-bold">Notifications</h2>
				<Dropdown
					trigger={["click"]}
					menu={{
						selectable: true,
						selectedKeys: [status],
						onSelect: ({ key }) => setStatus(key as NotificationFeedStatus),
						items: NOTIFICATION_STATUSES,
					}}
				>
					<Button
						type="link"
						iconPlacement="end"
						icon={<DownOutlined aria-hidden />}
						disabled={readAll.isPending || unreadCount === 0}
					>
						{humanReadable(status)}
					</Button>
				</Dropdown>
				<Button
					type="text"
					className="ml-auto"
					iconPlacement="end"
					loading={readAll.isPending}
					disabled={readAll.isPending || unreadCount === 0}
					icon={<CheckCircleOutlined aria-hidden />}
					onClick={handleReadAll}
				>
					Mark all as read
				</Button>
			</div>
			<Spinner spinning={isLoading}>
				<ul
					ref={setTargetScroll}
					aria-label={`${notifications.length} notification(s) available`}
					className="flex flex-col divide-y divide-grey-20 overflow-y-auto px-4 pt-5"
				>
					{notifications.map((notification) => {
						const { id, isRead, careOrgId, patient, createdAt, escalationId } =
							notification
						const [eventAriaLabel, eventDescription] = getActionText(
							notification,
							currentUser,
						)

						return (
							<li
								key={id}
								aria-label={
									!isRead ? `Unread: ${eventAriaLabel}` : eventAriaLabel
								}
								className="py-3 first:pt-0 last:pb-0"
							>
								<Wrap
									condition={!!careOrgId}
									with={(children) => (
										<Link
											to={`/${careOrgId}/patients/${patient.id}/escalations/${escalationId}?notificationId=${id}`}
										>
											{children}
										</Link>
									)}
								>
									<div className="flex gap-2">
										<div className="relative">
											{!isRead && (
												<>
													<span className="sr-only">Unread: </span>
													<div className="absolute -top-1 -left-1 size-1.5 rounded-full bg-blue-60" />
												</>
											)}
											<Avatar className="size-6! min-h-6! min-w-6! text-sm">
												{patient.fullName}
											</Avatar>
										</div>
										<div className="flex flex-col gap-1 text-navy-100">
											<div>{eventDescription}</div>
											<div className="text-navy-60">
												{asDate(createdAt).fromNow()}
											</div>
										</div>
									</div>
								</Wrap>
							</li>
						)
					})}
				</ul>
			</Spinner>
		</div>
	)
}

function getActionText(
	notification: Notification,
	currentUser: User,
): [string, ReactNode] {
	const { type, actor, patient, newAssignee, previousAssignee } = notification
	const isActor = actor?.id === currentUser.id
	const actorName = isActor ? "You" : (actor?.fullName ?? "Someone")
	const patientName = patient.fullName

	switch (type) {
		case "ESCALATION_ASSIGNED": {
			const isCurrentUser = !newAssignee || newAssignee.id === currentUser.id
			const assigneeText = isCurrentUser
				? isActor
					? "yourself"
					: "you"
				: newAssignee.fullName
			const verb = isActor ? "have assigned" : "has assigned"

			return [
				`${actorName} ${verb} ${assigneeText} a clinical escalation for ${patientName}`,
				<>
					{actorName} {verb} {assigneeText} a clinical escalation for{" "}
					<strong>{patientName}</strong>
				</>,
			]
		}
		case "ESCALATION_RESPONSE_ADDED": {
			return [
				`${actorName} responded to the clinical escalation for ${patientName}`,
				<>
					{actorName} responded to the clinical escalation for{" "}
					<strong>{patientName}</strong>
				</>,
			]
		}
		case "ESCALATION_REASSIGNED": {
			const isPrevCurrentUser = previousAssignee?.id === currentUser.id
			const isNewCurrentUser = newAssignee?.id === currentUser.id

			const prevText = previousAssignee
				? isPrevCurrentUser
					? isActor
						? "yourself"
						: "you"
					: previousAssignee.fullName
				: "unassigned"
			const newText = newAssignee
				? isNewCurrentUser
					? isActor
						? "yourself"
						: "you"
					: newAssignee.fullName
				: "unassigned"

			return [
				`${actorName} reassigned ${patientName}'s escalation from ${prevText} to ${newText}`,
				<>
					{actorName} reassigned <strong>{patientName}</strong>&apos;s{" "}
					escalation from {prevText} to {newText}
				</>,
			]
		}
		case "ESCALATION_CLOSED": {
			return [
				`${actorName} closed the escalation for ${patientName}`,
				<>
					{actorName} closed the escalation for <strong>{patientName}</strong>
				</>,
			]
		}
		case "ESCALATION_REOPENED": {
			return [
				`${actorName} reopened the escalation for ${patientName}`,
				<>
					{actorName} reopened the escalation for <strong>{patientName}</strong>
				</>,
			]
		}
		case "ASSIGNEE_REMINDER_24H": {
			return [
				`Reminder: a clinical escalation assigned to you for ${patientName} awaits your response`,
				<>
					Reminder: a clinical escalation assigned to you for{" "}
					<strong>{patientName}</strong> awaits your response
				</>,
			]
		}
		case "ASSIGNEE_REMINDER_48H": {
			return [
				`Reminder: a clinical escalation assigned to you for ${patientName} still awaits your response`,
				<>
					Reminder: a clinical escalation assigned to you for{" "}
					<strong>{patientName}</strong> still awaits your response
				</>,
			]
		}
		case "OBSERVER_REMINDER_48H": {
			return [
				`Reminder: a clinical escalation you follow for ${patientName} is still unopened`,
				<>
					Reminder: a clinical escalation you follow for{" "}
					<strong>{patientName}</strong> is still unopened
				</>,
			]
		}
		case "UNASSIGNED_AUTHOR_REMINDER": {
			return [
				`Reminder: your clinical escalation for ${patientName} still has no assignee`,
				<>
					Reminder: your clinical escalation for <strong>{patientName}</strong>{" "}
					still has no assignee
				</>,
			]
		}
		default: {
			return [
				`${actorName} updated the escalation for ${patientName}`,
				<>
					{actorName} updated the escalation for <strong>{patientName}</strong>
				</>,
			]
		}
	}
}
